import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { fromEvent, merge, Subscription } from 'rxjs';
import { AuthService } from '../auth.service';
import { LoginComponent } from '../login/login.component';
import { AUTH_CONSTANTS } from '../../shared/constants';

@Component({
  selector: 'app-session-monitor',
  standalone: true,
  imports: [CommonModule, FormsModule, LoginComponent],
  template: `
    <div *ngIf="showMagicLinkRequest" class="auth-modal">
      <div class="auth-modal-content">
        <h2>{{ isSessionExpiring ? 'Are you still there?' : 'Login Required' }}</h2>
        
        <!-- Session expiration warning -->
        <div *ngIf="isSessionExpiring">
          <p>Your session will expire in {{ timeRemaining }} seconds. Would you like to continue?</p>
          
          <div class="button-group">
            <button 
              (click)="refreshSession()" 
              class="btn btn-primary">
              Yes
            </button>
            <button 
              (click)="closeModal()" 
              class="btn btn-secondary">
              Log Out
            </button>
          </div>
        </div>
        
        <!-- Use the login component when not handling session expiration -->
        <div *ngIf="!isSessionExpiring" class="login-wrapper">
          <app-login></app-login>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .auth-modal {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(13,18,30,.45);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 1000;
    }
    .auth-modal-content {
      background: var(--ap-surface, #fff);
      padding: 1.5rem;
      border-radius: var(--ap-radius-lg, 14px);
      border: 1px solid var(--ap-border, #E3E6EB);
      width: 90%;
      max-width: 420px;
      color: var(--ap-text, #0B1220);
    }
    h2 {
      margin: 0 0 1rem 0;
      font-size: 0.9rem;
      font-weight: 600;
      color: var(--ap-text-muted, #6B7280);
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    .login-wrapper ::ng-deep .login-container {
      min-height: auto;
      background-color: transparent;
      padding: 0;
      margin: 0;
    }
    .login-wrapper ::ng-deep .login-card {
      box-shadow: none;
      border: none;
      border-radius: 0;
      padding: 0;
      width: 100%;
      max-width: 100%;
      background-color: transparent;
    }
    .form-group {
      margin-bottom: 1rem;
    }
    .form-control {
      width: 100%;
      padding: 7px 10px;
      border: 1px solid var(--ap-border, #E3E6EB);
      border-radius: var(--ap-radius-sm, 6px);
      font-size: 13px;
      background: var(--ap-surface, #fff);
      color: var(--ap-text, #0B1220);
      box-sizing: border-box;
    }
    .form-control:focus {
      outline: none;
      border-color: var(--ap-primary, #2F5BDA);
      box-shadow: var(--ap-ring, 0 0 0 3px rgba(47,91,218,.18));
    }
    .button-group {
      display: flex;
      justify-content: space-between;
      gap: 8px;
      margin-top: 1rem;
    }
    .btn {
      padding: 0 14px;
      height: 32px;
      border: 1px solid transparent;
      border-radius: var(--ap-radius-sm, 6px);
      cursor: pointer;
      font-size: 13px;
      font-weight: 550;
      transition: background 0.15s;
    }
    .btn-primary {
      background: var(--ap-primary, #2F5BDA);
      color: #fff;
      border-color: var(--ap-primary, #2F5BDA);
    }
    .btn-primary:hover:not(:disabled) {
      background: var(--ap-primary-ink, #1E3FA8);
      border-color: var(--ap-primary-ink, #1E3FA8);
    }
    .btn-secondary {
      background: var(--ap-surface-muted, #EEF0F3);
      color: var(--ap-text-soft, #4B5563);
      border-color: var(--ap-border, #E3E6EB);
    }
    .btn-secondary:hover:not(:disabled) {
      background: var(--ap-border, #E3E6EB);
    }
    .btn:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
    .alert {
      margin-top: 1rem;
      padding: 10px 12px;
      border-radius: var(--ap-radius-sm, 6px);
      font-size: 13px;
    }
    .alert-success {
      background: var(--ap-success-soft, #D1FAE5);
      color: var(--ap-success, #047A55);
      border: 1px solid rgba(4,122,85,.2);
    }
    .alert-danger {
      background: var(--ap-danger-soft, #FEE2E2);
      color: var(--ap-danger, #B42318);
      border: 1px solid rgba(180,35,24,.2);
    }
  `]
})
export class SessionMonitorComponent implements OnInit, OnDestroy {
  public showMagicLinkRequest = false;
  public isSessionExpiring = false;
  // Session expiration related properties
  public timeRemaining = AUTH_CONSTANTS.SESSION_EXPIRING_THRESHOLD;
  
  private userSubscription: Subscription | null = null;
  private logoutTimer: any = null;
  private inactivityTimer: any = null;
  private activitySubscription: Subscription | null = null;
  // Flag to track if initialization has been completed
  private initialized: boolean = false;
  
  constructor(private authService: AuthService) {}
  
  ngOnInit(): void {
    // Prevent multiple initializations
    if (true === this.initialized) {
      console.log('SessionMonitorComponent already initialized, skipping duplicate initialization');
      return;
    }
    
    this.initialized = true;
    console.log('Initializing SessionMonitorComponent');
    
    // Check if user is already logged in
    this.userSubscription = this.authService.currentUser$.subscribe(user => {
      console.log('SessionMonitor: User state changed, user:', user);
      if (!user) {
        // Only show login modal if we're not on the login page or magic link page
        const currentUrl = window.location.pathname;
        console.log('SessionMonitor: Current URL:', currentUrl);
        if (!AUTH_CONSTANTS.AUTH_EXEMPT_ROUTES.some(route => currentUrl === route || currentUrl.startsWith(route)) && 
            false === currentUrl.includes('/magiclink')) {
          console.log('SessionMonitor: Showing login modal');
          this.showMagicLinkRequest = true;
          this.isSessionExpiring = false;
        } else {
          // We're on the login page or magic link page, don't show the modal
          console.log('SessionMonitor: On exempt route, not showing login modal');
          this.showMagicLinkRequest = false;
        }
      } else {
        // User is logged in, hide the login modal
        console.log('SessionMonitor: User is logged in, hiding login modal');
        this.showMagicLinkRequest = false;
        // Listen for session expiration events
        this.listenForSessionExpiration();
        // Start inactivity tracking
        this.startInactivityTracking();
      }
    });
    
    // Check for token in URL on component init
    this.authService.validateTokenFromUrl();
  }
  
  ngOnDestroy(): void {
    console.log('Destroying SessionMonitorComponent');
    if (this.userSubscription) {
      console.log('Cleaning up user subscription');
      this.userSubscription.unsubscribe();
      this.userSubscription = null;
    }
    this.clearLogoutTimer();
    this.stopInactivityTracking();
    this.initialized = false;
  }
  
  private listenForSessionExpiration(): void {
    console.log('Setting up session expiration listener');
    
    // Prevent multiple subscriptions
    if (this.userSubscription) {
      console.log('Session expiration listener already exists, cleaning up before creating a new one');
      this.userSubscription.unsubscribe();
      this.userSubscription = null;
    }
    
    // Track if we've already shown the expiry UI to prevent duplicate notifications
    let hasShownExpiryUI = false;
    
    // This will be triggered when the token is about to expire
    // The AuthService will emit a value on currentUser$ when the token is close to expiration
    this.userSubscription = this.authService.currentUser$.subscribe(user => {
      if (user) {
        // User is logged in, make sure login modal is hidden
        this.showMagicLinkRequest = false;
        this.isSessionExpiring = false;
        
        // If we get a user but the token is about to expire, show the session expiring modal
        const token = this.authService.getToken();
        
        // Skip excessive logging to reduce console spam
        if (!hasShownExpiryUI) {
          console.log('User subscription triggered:', `User ${user.email} logged in`);
          console.log('Current token exists:', null !== token);
        }
        
        if (token) {
          const decodedToken = this.decodeJwt(token);
          
          if (!hasShownExpiryUI) {
            console.log('Token decoded:', null !== decodedToken);
          }
          
          if (decodedToken) {
            const currentTime = Math.floor(Date.now() / 1000);
            const timeToExpiry = decodedToken.exp - currentTime;
            
            if (!hasShownExpiryUI) {
              console.log('Token expiration details:', {
                currentTime,
                expiryTime: decodedToken.exp,
                timeToExpiry: `${timeToExpiry} seconds (${Math.floor(timeToExpiry / 60)} minutes)`,
                isExpiringSoon: AUTH_CONSTANTS.SESSION_EXPIRING_THRESHOLD >= timeToExpiry
              });
            }
            
            // Check if token is already expired
            if (0 >= timeToExpiry) {
              console.log('Token is expired, logging out');
              this.clearLogoutTimer();
              this.authService.logout();
              return;
            }
            
            // If less than or equal to the threshold seconds remain, show the session expiring UI
            if (AUTH_CONSTANTS.SESSION_EXPIRING_THRESHOLD >= timeToExpiry && !hasShownExpiryUI) {
              console.log('Session is expiring soon, showing session expiration UI');
              hasShownExpiryUI = true;
              
              // Force the UI update by using setTimeout to ensure it runs after the current execution context
              setTimeout(() => {
                this.showMagicLinkRequest = true;
                this.isSessionExpiring = true;
                this.timeRemaining = Math.max(Math.floor(timeToExpiry), 0);
                
                // Set up countdown timer
                this.startExpiryCountdown(this.timeRemaining);
                console.log('Session expiration UI should now be visible', {
                  showMagicLinkRequest: this.showMagicLinkRequest,
                  isSessionExpiring: this.isSessionExpiring,
                  timeRemaining: this.timeRemaining
                });
              }, 0);
            } else if (timeToExpiry > AUTH_CONSTANTS.SESSION_EXPIRING_THRESHOLD) {
              // Only log when not in expiry state to reduce console spam
              if (!hasShownExpiryUI) {
                console.log('Session not expiring soon, no UI changes needed');
              }
              
              // If the token has been refreshed and is now valid again, hide the UI
              if (this.showMagicLinkRequest && this.isSessionExpiring) {
                console.log('Token has been refreshed, hiding session expiration UI');
                this.showMagicLinkRequest = false;
                this.isSessionExpiring = false;
                this.clearLogoutTimer();
                hasShownExpiryUI = false; // Reset so we can show again in the future
              }
            }
          }
        }
      } else {
        console.log('No user detected, user has logged out');
        this.clearLogoutTimer();
        this.stopInactivityTracking();
        hasShownExpiryUI = false; // Reset for next login
        
        // Show login modal when user logs out
        const currentUrl = window.location.pathname;
        if (!AUTH_CONSTANTS.AUTH_EXEMPT_ROUTES.some(route => currentUrl === route || currentUrl.startsWith(route)) && 
            false === currentUrl.includes('/magiclink')) {
          console.log('SessionMonitor: User logged out, showing login modal');
          this.showMagicLinkRequest = true;
          this.isSessionExpiring = false;
        }
      }
    });
    
    console.log('Session expiration listener setup complete');
  }
  
  private decodeJwt(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      return JSON.parse(window.atob(base64));
    } catch (error) {
      return null;
    }
  }
  
  // Login functionality is now handled by the LoginComponent
  
  public refreshSession(): void {
    console.log('User requested to refresh session');
    
    // Clear the logout timer while we're refreshing
    this.clearLogoutTimer();
    
    // Call the auth service to refresh the token
    this.authService.refreshToken().subscribe({
      next: (response: any) => {
        if (response && response.token) {
          // Update the token
          this.authService.updateToken(response.token);
          
          // Hide the session expiration modal
          this.showMagicLinkRequest = false;
          this.isSessionExpiring = false;
          
          console.log('Session refreshed successfully');
        } else {
          console.error('Refresh token response missing token:', response);
          
          // Force logout after a short delay
          setTimeout(() => {
            this.authService.logout();
          }, AUTH_CONSTANTS.LOGOUT_DELAY_MS);
        }
      },
      error: (err: any) => {
        console.error('Failed to refresh session:', err);
        
        // Force logout after a short delay
        setTimeout(() => {
          this.authService.logout();
        }, 2000);
      }
    });
  }
  
  public closeModal(): void {
    if (this.isSessionExpiring) {
      // If the session is expiring, logout
      this.authService.logout();
    } else {
      // Just close the modal without logging out
      this.showMagicLinkRequest = false;
    }
  }
  
  private startExpiryCountdown(seconds: number): void {
    console.log(`Starting expiry countdown from ${seconds} seconds`);
    
    // Clear any existing timer
    this.clearLogoutTimer();
    
    // Set the initial value
    this.timeRemaining = seconds;
    
    // Create a countdown timer that updates every second
    this.logoutTimer = setInterval(() => {
      this.timeRemaining--;
      
      // Log every 5 seconds to avoid excessive logging
      if (0 === this.timeRemaining % 5) {
        console.log(`Session expiry countdown: ${this.timeRemaining} seconds remaining`);
      }
      
      if (1 > this.timeRemaining) {
        console.log('Session expired, logging out automatically');
        this.clearLogoutTimer();
        this.authService.logout();
      }
    }, 1000);
    
    console.log('Expiry countdown timer started');
  }
  
  private clearLogoutTimer(): void {
    if (null !== this.logoutTimer) {
      console.log('Clearing logout timer');
      clearInterval(this.logoutTimer);
      this.logoutTimer = null;
    }
  }

  private startInactivityTracking(): void {
    this.stopInactivityTracking();
    console.log('Starting inactivity tracking (4 hour timeout)');

    this.activitySubscription = merge(
      fromEvent(document, 'mousemove'),
      fromEvent(document, 'mousedown'),
      fromEvent(document, 'keydown'),
      fromEvent(document, 'touchstart'),
      fromEvent(document, 'scroll')
    ).subscribe(() => this.resetInactivityTimer());

    this.resetInactivityTimer();
  }

  private resetInactivityTimer(): void {
    if (null !== this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }
    this.inactivityTimer = setTimeout(() => {
      console.log('Inactivity timeout reached (4 hours), logging out');
      this.authService.logout();
    }, AUTH_CONSTANTS.INACTIVITY_TIMEOUT_MS);
  }

  private stopInactivityTracking(): void {
    if (null !== this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
    if (null !== this.activitySubscription) {
      this.activitySubscription.unsubscribe();
      this.activitySubscription = null;
    }
  }
}
